"use client";

/**
 * V2 Landing Page Public Wrapper
 * Shows organizer FAB, chatbot widget, and translate button.
 * Password protection is handled server-side in the route via V2PasswordGate.
 * Access control (public/invited/private) is handled server-side in the route.
 */

import type React from "react";
import { useState } from "react";
import { Pencil } from "lucide-react";
import { TranslatorProvider } from "eqqo-react";
import { FetchToastProvider } from "@/components/providers/FetchToastProvider";
import ChatbotWidget from "@/features/landing-pages/_components/public/ChatbotWidget";
import { buildLandingPageManagePath } from "@/lib/routes/landing-page-manage-paths";
import { armScheduleClock } from "../../_logic/utils/schedule-clock";
import type { EventLandingPagePublicRow } from "../../_logic/types/db-types";
import type { PublicPageInitialData } from "../../_logic/types/public-page-data";
import { DEFAULT_PRIMARY_COLOR } from "../../_logic/constants";
import type { PageComponent } from "../../_types";
import type { ViewerIdentity } from "../../_logic/actions/_shared/get-invited-page-access";
import type { ScheduleBannerProps } from "../../_logic/actions/_shared/schedule-banner";
import { V2LandingPagePublicView } from "./V2LandingPagePublicView";
import { TranslateButton } from "./TranslateButton";

/**
 * Server-resolved viewer access, all sourced from one `getInvitedPageAccess` call. Grouped into a
 * single prop so the wrapper's prop list stays lean (these always travel together).
 */
export interface PublicViewerAccess {
  /** Viewer identity for signup prefill. Null when the visitor isn't signed in / not allowed. */
  viewer: ViewerIdentity | null;
  /** Opportunistic FK to nowadays.attendees if the viewer's email matched the event roster. */
  attendeeId: number | null;
  /**
   * Server-resolved: is the viewer an authenticated Nowadays admin. The ONLY input that enables the
   * `?__now=` schedule clock override in production (see `armScheduleClock`). `false` for logged-out /
   * non-admin viewers keeps production's "ignored" behavior.
   */
  isNowadaysAdmin: boolean;
  /**
   * Server-resolved: owner/admin via `user_event_access`, or enterprise group access. Drives the
   * "Edit Landing Page" FAB and the activity-card organizer menu (ORed with `isNowadaysAdmin` above).
   */
  canManageEvent: boolean;
  /**
   * Server-resolved: may the viewer preview `test_publish` activities — event owner/admin,
   * Nowadays admin, or enterprise event access. Drives visibility of `test_publish` activities
   * and their "Test" badge. Must stay `false` for anyone not in that union — a `test_publish`
   * activity is hidden from everyone else regardless of `publish_at`/`invite_only`.
   */
  canPreviewTestPublish: boolean;
}

/** Default viewer access for the logged-out / not-resolved case (stable reference, fail-closed). */
const NO_VIEWER_ACCESS: PublicViewerAccess = {
  viewer: null,
  attendeeId: null,
  isNowadaysAdmin: false,
  canManageEvent: false,
  canPreviewTestPublish: false,
};

interface V2LandingPagePublicWrapperProps {
  page: EventLandingPagePublicRow;
  components: PageComponent[];
  /** Event-level IANA timezone (hero, activities, schedule, .ics). NULL = floating time. */
  eventTimeZone: string | null;
  /** Schedule data-fetch + live-banner props, resolved server-side (see `resolveScheduleBannerProps`). */
  scheduleBanner: ScheduleBannerProps;
  /** Parent page slug — set when rendering a subpage so the edit link navigates correctly */
  parentSlug?: string;
  /**
   * Parent page name — set when rendering a subpage so the chatbot welcome
   * line names the overall event ("...questions about Spring Summit") instead
   * of whichever subpage the visitor landed on ("...questions about Schedule").
   */
  parentName?: string;
  /** Server-resolved viewer access (identity + attendee FK + admin/manage flags). See {@link PublicViewerAccess}. */
  viewerAccess?: PublicViewerAccess;
  /**
   * Server-fetched initial data for the interactive sections (activities, schedule, signup counts,
   * viewer registrations), resolved in the RSC route and seeded into the provider tree so the
   * sections render with data on first paint. Undefined when the caller hasn't prefetched.
   */
  initialData?: PublicPageInitialData | null;
}

/**
 * Client wrapper showing organizer edit button, chatbot, and translate features.
 *
 * @param props - See V2LandingPagePublicWrapperProps.
 * @returns The wrapped public landing page.
 */
export function V2LandingPagePublicWrapper({
  page,
  components,
  eventTimeZone,
  scheduleBanner,
  parentSlug,
  parentName,
  viewerAccess = NO_VIEWER_ACCESS,
  initialData = null,
}: V2LandingPagePublicWrapperProps): React.ReactElement {
  const {
    viewer,
    attendeeId,
    isNowadaysAdmin: viewerIsNowadaysAdmin,
    canManageEvent,
    canPreviewTestPublish,
  } = viewerAccess;

  // Arm the schedule clock with the server-resolved admin flag. Deliberately called in the render
  // body (not an effect): `useNow`'s `useState(() => scheduleNow())` reads the clock at mount, so the
  // flag must be armed before children render — an effect runs too late and would miss that first
  // read. Safe to do here because `armScheduleClock` is idempotent (no-op when the value is
  // unchanged), so StrictMode's double-render and re-renders cost nothing. Do NOT "fix" into a
  // useEffect.
  armScheduleClock(viewerIsNowadaysAdmin);

  // States
  // The mobile chat is a full-screen sheet; the language selector floats above it and covers the composer.
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Derived values
  // Both inputs are server-resolved by the same getInvitedPageAccess call — present on first
  // paint, no client-side re-fetch or FAB pop-in for anyone, including Nowadays admins.
  const canEditPage = canManageEvent || viewerIsNowadaysAdmin;

  // Render
  return (
    <TranslatorProvider publicKey={process.env.NEXT_PUBLIC_EQQO_PUBLIC_KEY ?? ""} defaultLanguage="en">
      <V2LandingPagePublicView
        page={page}
        components={components}
        eventTimeZone={eventTimeZone}
        scheduleBanner={scheduleBanner}
        // On a subpage `parentSlug` is the top-level slug; on the main page it's undefined, so fall
        // back to this page's own slug (which is the top-level there).
        rootSlug={parentSlug ?? page.slug}
        isOrganizer={canEditPage}
        viewer={viewer}
        attendeeId={attendeeId}
        canPreviewTestPublish={canPreviewTestPublish}
        initialData={initialData}
      />
      <TranslateButton isHiddenOnMobile={isChatOpen}>
        {canEditPage && (
          <a
            href={buildLandingPageManagePath(parentSlug ?? page.slug, {
              subpage: parentSlug ? page.slug : null,
            })}
            className="flex touch-manipulation items-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white shadow-[0_4px_16px_rgba(0,0,0,0.25)] transition-colors hover:bg-gray-800 hover:shadow-[0_6px_20px_rgba(0,0,0,0.3)]"
          >
            <Pencil className="h-4 w-4" />
            <span className="hidden sm:inline">Edit Landing Page</span>
          </a>
        )}
      </TranslateButton>
      {page.chatbot_enabled && (
        <FetchToastProvider>
          <ChatbotWidget
            pageId={page.id}
            pageName={parentName || page.name}
            primaryColor={page.primary_color ?? DEFAULT_PRIMARY_COLOR}
            onOpenChange={setIsChatOpen}
          />
        </FetchToastProvider>
      )}
    </TranslatorProvider>
  );
}
